import React from "react";
import { Routes, Route, Outlet, useParams, useLocation, Navigate } from "react-router-dom";
import { TenantProvider, useTenant } from "@/lib/TenantContext";
import { api } from "@/lib/api";
import { isTenantSuspendedError } from "@/lib/apiErrors";
import { useT } from "@/lib/LanguageContext";
import { useBrandingTheme } from "@/lib/useBrandingTheme";
import SiteTemplateRenderer from "@/templates/SiteTemplateRenderer";
import { hostOwnsSlug } from "@/tenant/legacyTenantPath";

import Reserver from "@/public/Reserver";
import ConsultBooking from "@/public/ConsultBooking";
import ConsultRoom from "@/public/ConsultRoom";
import Boutique from "@/public/Boutique";
// crm-module BEGIN C2.03 (lane F2) — public session-pack purchase
import PackPurchase from "@/public/packs/PackPurchase";
// crm-module END
import Programmes from "@/public/Programmes";
import Parcours from "@/public/Parcours";
import PathwayMember from "@/public/PathwayMember";
import PathwayStep from "@/public/pathways/PathwayStep";
import NudgesOff from "@/public/pathways/NudgesOff";
import JournalIndex from "@/public/JournalIndex";
import JournalPost from "@/public/JournalPost";
import ClientPortal from "@/public/ClientPortal";
import ManageBooking from "@/public/ManageBooking";
import ReviewForm from "@/public/ReviewForm";
// crm-module BEGIN C2.06 (lane F1) — reminder Confirm + marketing-footer Unsubscribe
import BookingConfirm from "@/public/crm/BookingConfirm";
import EmailUnsubscribe from "@/public/crm/EmailUnsubscribe";
// crm-module END
import CheckoutSuccess from "@/public/CheckoutSuccess";

import Login from "@/admin/Login";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLayout from "@/admin/AdminLayout";
import Overview from "@/admin/Overview";
import BlogAdmin from "@/admin/BlogAdmin";
import TestimonialsAdmin from "@/admin/TestimonialsAdmin";
import MessagesAdmin from "@/admin/MessagesAdmin";
import AvailabilityAdmin from "@/admin/AvailabilityAdmin";
import BookingsAdmin from "@/admin/BookingsAdmin";
import BookingCalendar from "@/admin/BookingCalendar";
import ServicesAdmin from "@/admin/booking/ServicesAdmin";
import ContentAdmin from "@/admin/ContentAdmin";
import MediaAdmin from "@/admin/MediaAdmin";
import ProductsAdmin from "@/admin/ProductsAdmin";
import ProgramsAdmin from "@/admin/ProgramsAdmin";
import PromosAdmin from "@/admin/PromosAdmin";
import GiftCardsAdmin from "@/admin/GiftCardsAdmin";
import InvoicesAdmin from "@/admin/InvoicesAdmin";
import SettingsAdmin from "@/admin/SettingsAdmin";
import SocialAdmin from "@/admin/SocialAdmin";
import AiAssistantAdmin from "@/admin/AiAssistantAdmin";
import BrandingAdmin from "@/admin/BrandingAdmin";
import PathwaysAdmin from "@/admin/PathwaysAdmin";
import PathwayBuilder from "@/admin/pathways/PathwayBuilder";
import PathwayDashboard from "@/admin/pathways/PathwayDashboard";
import PathwayPreview from "@/admin/pathways/PathwayPreview";
import ModalityPacksAdmin from "@/admin/ModalityPacksAdmin";
import CustomDomainAdmin from "@/admin/CustomDomainAdmin";
import AffiliateAdmin from "@/admin/affiliate/AffiliateAdmin";
import ReviewsAdmin from "@/admin/reviews/ReviewsAdmin";
import PublicProfileAdmin from "@/admin/PublicProfileAdmin";
import OnboardingWizard from "@/admin/OnboardingWizard";
import BillingAdmin from "@/admin/BillingAdmin";
import ComingSoonAdmin from "@/admin/ComingSoonAdmin";
// Routes admin manquantes sur les hôtes tenant (custom domain / sous-domaine) : sans elles,
// les liens de la sidebar (adminNav.js) rendaient une page blanche. À garder synchronisé avec
// routes/adminRoutes.jsx (duplication connue).
import TelehealthAdmin from "@/admin/TelehealthAdmin";
import ConsultRoomPage from "@/admin/telehealth/ConsultRoomPage";
import PaymentsAdmin from "@/admin/PaymentsAdmin";
import TaxAdmin from "@/admin/TaxAdmin";
import LearnAdmin from "@/admin/LearnAdmin";
import LearnAnalyticsAdmin from "@/admin/LearnAnalyticsAdmin";
import QuizReviewsAdmin from "@/admin/QuizReviewsAdmin";
import SiteBuilderWizard from "@/admin/SiteBuilderWizard";
import SiteImagesAdmin from "@/admin/SiteImagesAdmin";
import RequireCapability from "@/admin/entitlements/RequireCapability";
import { CAP } from "@/admin/entitlements/capabilities";
// crm-module BEGIN C1.01
import CrmShell from "@/admin/crm/CrmShell";
import { crmRouteChildren } from "@/admin/crm/crmRoutes";
// crm-module END
// crm-module BEGIN C3.02 (lane F5)
import JoinPage from "@/admin/crm/JoinPage";
// crm-module END
// crm-module BEGIN C3.02 — helper shell scoping (decision 5)
import RequireOwnerAdmin from "@/admin/RequireOwnerAdmin";
// crm-module END
import LearnCatalogue from "@/public/LearnCatalogue";
import LearnPlayer from "@/public/LearnPlayer";
import TermsAndConditions from "@/saas/TermsAndConditions";
import LegalPage from "@/saas/LegalPage";
import SupportPage from "@/saas/SupportPage";

const TenantLanding = () => {
  const { tenant, loading } = useTenant();
  if (loading) return <div className="min-h-screen bg-eh-bg" />;
  if (!tenant) return <Navigate to="/platform" replace />;
  return <SiteTemplateRenderer tenant={tenant} />;
};

/** Shown when a request names a practice this host cannot serve. */
const SiteNotFound = () => {
  const t = useT("common");
  return (
    <div data-testid="site-not-found" className="min-h-screen flex flex-col items-center justify-center bg-eh-bg text-center px-6">
      <h1 className="text-2xl font-semibold text-eh-ink">{t("errors.siteNotFoundTitle")}</h1>
      <p className="mt-3 text-eh-muted">{t("errors.siteNotFoundBody")}</p>
    </div>
  );
};

/**
 * Shown when the host DOES name a real practice, but the platform is not serving it right
 * now (today: suspended — non-payment or a platform-side decision).
 *
 * Deliberately says nothing about suspension, billing or payment: the reader is a client
 * of the practice, not the party who owes anything, and leaking a practitioner's billing
 * state to their clientele is both a confidentiality break and a business injury. It also
 * must not say "not found" — that tells a returning client the practice no longer exists
 * and is the one message they act on permanently.
 */
const SiteUnavailable = () => {
  const t = useT("common");
  return (
    <div data-testid="site-unavailable" className="min-h-screen flex flex-col items-center justify-center bg-eh-bg text-center px-6">
      <h1 className="text-2xl font-semibold text-eh-ink">{t("errors.siteUnavailableTitle")}</h1>
      <p className="mt-3 text-eh-muted">{t("errors.siteUnavailableBody")}</p>
    </div>
  );
};

/**
 * The public site of a practice the platform is NOT serving, on the PLATFORM host.
 *
 * `TenantSiteHost` gates the tenant-host tree at its own bootstrap (GET /public/site,
 * refused by tenant_middleware). The `/t/{slug}` tree has a DIFFERENT bootstrap — GET
 * /api/saas/tenant/{slug} through TenantProvider — and `/api/saas/` is in the
 * middleware's NEVER_SCOPED list, so nothing there ever looked at the practice's status.
 * The site therefore rendered, with fallback copy, while every content call behind it
 * answered 403. `saas_routes.get_tenant_public` now applies the same serving rule, and
 * this is the screen its refusal has to produce. The URL is a live one: AdminLayout builds
 * "View site" as `/t/{slug}` for every practice with no custom domain.
 *
 * NOT a gate over `tenant == null`: an unresolved practice (404 — no such slug) is a
 * different message, and each page below already decides what to do with it (the landing
 * redirects to the platform site). Only the "exists, not served" verdict lands here.
 *
 * Scope is deliberate — it wraps the PUBLIC pages, never the console. That mirrors
 * tenant_middleware, which keeps `/api/admin/` reads alive on a suspended practice
 * (_TENANT_ADMIN_PREFIXES) precisely so the dashboard still displays and
 * `/admin/billing`, the recovery path, stays reachable. `/t/{slug}/admin` is also the link
 * the super-admin console opens a practice's dashboard with, and
 * `/t/{slug}/admin/onboarding` is where a brand-new tenant lands right after subscribing
 * — its status is `pending`, i.e. NOT served, so darkening the console would break the
 * onboarding of every new customer.
 */
import FloatingChatWidget from "@/components/site/FloatingChatWidget";

export const ServedSiteGate = () => {
  const { unavailable } = useTenant();
  if (unavailable) return <SiteUnavailable />;
  return <><Outlet /><FloatingChatWidget /></>;
};

// On a tenant host the practice is already fixed by the Host header, so the internal
// "/t/{slug}" path prefix is redundant here — and it is routed nowhere in this tree, so
// it rendered a permanently BLANK page. AdminLayout builds the sidebar's "View site"
// link as "/t/{slug}" whenever the tenant has no custom domain, which means the link is
// reachable on exactly the hosts where it does not resolve.
//
// The redirect is guarded rather than unconditional: answering a request for SOMEONE
// ELSE's practice with this practice's home page would render the wrong identity under
// a URL that asked for a different one — the same class of defect as the hardcoded
// branding. A slug this host does not own gets the not-found page instead.
const LegacyTenantPathRedirect = () => {
  const { slug: pathSlug, "*": rest } = useParams();
  const { tenant, loading, slug: hostSlug } = useTenant();
  if (loading) return <div className="min-h-screen bg-eh-bg" />;
  if (!hostOwnsSlug(pathSlug, tenant, hostSlug)) return <SiteNotFound />;
  return <Navigate to={`/${rest || ""}`} replace />;
};

const TenantRoutes = () => {
  const { tenant } = useTenant();
  useBrandingTheme(tenant?.branding);
  return (
    <Routes>
      {/* Every page of the practice's PUBLIC site sits behind ServedSiteGate (pathless
          layout route): one enforcement point for "this practice is not being served",
          instead of the same check repeated on twelve pages. The console below is
          deliberately outside it — see the gate's own comment. */}
      <Route element={<ServedSiteGate />}>
        <Route index element={<TenantLanding />} />
        <Route path="reserver" element={<Reserver />} />
        {/* Flow C (per-minute telehealth) — same route, same reasoning, as
            routes/platformRoutes.jsx's registration. */}
        <Route path="consultation/confirmer" element={<ConsultBooking />} />
        {/* The patient's standing room page (BUG #3 fix) — same registration reasoning
            as routes/platformRoutes.jsx. */}
        <Route path="consultation/salle" element={<ConsultRoom />} />
        <Route path="boutique" element={<Boutique />} />
        {/* crm-module BEGIN C2.03 (lane F2) — public session-pack purchase */}
        <Route path="forfaits" element={<PackPurchase />} />
        {/* crm-module END */}
        <Route path="programmes" element={<Programmes />} />
        {/* Pathways (contract §9): same routes, same registration reasoning as
            routes/platformRoutes.jsx. */}
        <Route path="parcours" element={<Parcours />} />
        <Route path="parcours/nudges-off" element={<NudgesOff />} />
        <Route path="parcours/:pathwayId" element={<PathwayMember />} />
        <Route path="parcours/:pathwayId/etape/:stepId" element={<PathwayStep />} />
        <Route path="journal" element={<JournalIndex />} />
        <Route path="journal/:slug" element={<JournalPost />} />
        {/* Cours publics : ClientPortal lie vers tenantHref("/learn[/:courseId]") ; sans ces routes
            les CTA « Découvrir les cours » / « Continuer » rendaient une page blanche sur les hôtes tenant. */}
        <Route path="learn" element={<LearnCatalogue />} />
        <Route path="learn/:courseId" element={<LearnPlayer />} />
        <Route path="compte" element={<ClientPortal />} />
        {/* No-login self-service link mailed with every booking confirmation
            (manage_token in the query string) — same unprefixed pattern as "compte". */}
        <Route path="gerer-rdv" element={<ManageBooking />} />
        {/* The review form — unprefixed, same no-login capability-link pattern as
            "gerer-rdv" and "compte", and the origin the mail actually points at. */}
        <Route path="avis" element={<ReviewForm />} />
        {/* crm-module BEGIN C2.06 (lane F1) — the reminder email's Confirm button and the
            marketing-footer Unsubscribe link, same no-login capability-link pattern as
            "gerer-rdv" and "avis". Registered on BOTH trees (here and
            routes/platformRoutes.jsx). */}
        <Route path="rdv/confirmer" element={<BookingConfirm />} />
        <Route path="desabonnement" element={<EmailUnsubscribe />} />
        {/* crm-module END */}
        <Route path="merci/:kind" element={<CheckoutSuccess />} />
        <Route path="terms" element={<TermsAndConditions />} />
        <Route path="terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="terms-of-service" element={<TermsAndConditions />} />
        <Route path="cgu" element={<TermsAndConditions />} />
        <Route path="cgv" element={<TermsAndConditions />} />
        <Route path="legal/:doc" element={<LegalPage />} />
        <Route path="contact" element={<SupportPage />} />
        <Route path="support" element={<SupportPage />} />
      </Route>
      {/* Internal "/t/{slug}/…" links followed on a tenant host — see above. */}
      <Route path="t/:slug/*" element={<LegacyTenantPathRedirect />} />
      <Route path="admin/login" element={<Login />} />
      {/* crm-module BEGIN C3.02 (lane F5) — same reasoning as routes/adminRoutes.jsx: an
          invited helper owns no tenant yet, so this must sit outside ProtectedRoute. */}
      <Route path="admin/join" element={<JoinPage />} />
      {/* crm-module END */}
      <Route path="admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        {/* crm-module BEGIN C1.01 — the CRM section: layout route + the shared child fragment (admin/crm/crmRoutes.jsx) */}
        <Route path="crm" element={<RequireCapability capability={CAP.crm}><CrmShell /></RequireCapability>}>{crmRouteChildren}</Route>
        {/* crm-module END */}
        {/* crm-module BEGIN C3.02 — helper shell scoping (decision 5, role matrix). ONE
            pathless guard over every sibling of `crm` above, on BOTH hosts
            (routes/adminRoutes.jsx carries the text-identical line) — not a check added
            to each page below. A CRM helper is redirected to `crm` here before any of
            these thirty-odd pages mounts and discovers, each on its own, the 403 the
            server was always going to answer it with. */}
        <Route element={<RequireOwnerAdmin />}>
        {/* crm-module END */}
        <Route index element={<Overview />} />
        <Route path="blog" element={<BlogAdmin />} />
        <Route path="testimonials" element={<TestimonialsAdmin />} />
        <Route path="messages" element={<MessagesAdmin />} />
        <Route path="availability" element={<AvailabilityAdmin />} />
        <Route path="bookings" element={<BookingsAdmin />} />
        <Route path="calendar" element={<BookingCalendar />} />
        {/* Catalogue de services tarifés : sans cette ligne, le lien de la sidebar rendait
            une PAGE BLANCHE sur un hôte tenant — donc la réservation payante était
            inatteignable depuis le tableau de bord que les praticiens utilisent vraiment
            (sous-domaine / domaine personnalisé). C'est exactement la duplication que le
            commentaire en tête de ce bloc signale. */}
        <Route path="booking-services" element={<ServicesAdmin />} />
        {/* The /admin/services alias, text-identical to routes/adminRoutes.jsx: RELATIVE,
            so /t/{slug}/admin/services stays inside that practice's console. */}
        <Route path="services" element={<Navigate to="../booking-services" replace />} />
        <Route path="products" element={<ProductsAdmin />} />
        <Route path="programs" element={<ProgramsAdmin />} />
        <Route path="promos" element={<PromosAdmin />} />
        <Route path="giftcards" element={<GiftCardsAdmin />} />
        <Route path="invoices" element={<InvoicesAdmin />} />
        <Route path="content" element={<ContentAdmin />} />
        <Route path="media" element={<MediaAdmin />} />
        <Route path="site-images" element={<SiteImagesAdmin />} />
        <Route path="settings" element={<SettingsAdmin />} />
        {/* crm-module BEGIN C1.01 — the old Clients page is the CRM Clients list now */}
        <Route path="customers" element={<Navigate to="../crm/clients" replace />} />
        {/* crm-module END */}
        {/* NOT plan-gated, deliberately: paid consultations must stay joinable and
            completable after a downgrade. The page withholds only NEW video business
            (TelehealthAdmin, ServicesAdmin); care.video_rooms is enforced server-side. */}
        <Route path="telehealth" element={<TelehealthAdmin />} />
        {/* The practitioner's per-minute consult room (BUG #3 fix) — same registration
            reasoning as routes/adminRoutes.jsx. */}
        <Route path="telehealth/consult/:appointmentId" element={<ConsultRoomPage />} />
        <Route path="payments" element={<PaymentsAdmin />} />
        <Route path="tax-config" element={<TaxAdmin />} />
        <Route path="ai" element={<RequireCapability capability={CAP.aiAssistant}><AiAssistantAdmin /></RequireCapability>} />
        {/* crm-module BEGIN C2.05 — /admin/automation is retired: Email Automation lives at
            /admin/crm/email (decision 8, Sequences replace the old rule engine); no redirect,
            the old page is gone. */}
        {/* crm-module END */}
        <Route path="social" element={<RequireCapability capability={CAP.social}><SocialAdmin /></RequireCapability>} />
        <Route path="branding" element={<BrandingAdmin />} />
        <Route path="profile" element={<PublicProfileAdmin />} />
        <Route path="site-builder" element={<SiteBuilderWizard />} />
        {/* Academy pages are NOT plan-gated: the server gates course authoring only,
            and a downgraded practice still grades and follows its students.
            LearnAdmin withholds its own authoring controls. */}
        <Route path="learn" element={<LearnAdmin />} />
        <Route path="learn-analytics" element={<LearnAnalyticsAdmin />} />
        <Route path="quiz-reviews" element={<QuizReviewsAdmin />} />
        <Route path="pathways" element={<PathwaysAdmin />} />
        {/* Pathways v3 (contract §9): the same four routes as routes/adminRoutes.jsx. */}
        <Route path="pathways/new" element={<PathwayBuilder />} />
        <Route path="pathways/:pathwayId/edit" element={<PathwayBuilder />} />
        <Route path="pathways/:pathwayId/preview" element={<PathwayPreview />} />
        <Route path="pathways/:pathwayId" element={<PathwayDashboard />} />
        <Route path="modality-packs" element={<ModalityPacksAdmin />} />
        {/* NOT plan-gated: a downgraded practice must still see its domain. Setting and
            verifying one is withheld inside CustomDomainAdmin. */}
        <Route path="custom-domain" element={<CustomDomainAdmin />} />
        <Route path="affiliate" element={<AffiliateAdmin />} />
        <Route path="reviews" element={<ReviewsAdmin />} />
        <Route path="onboarding" element={<OnboardingWizard />} />
        <Route path="billing" element={<BillingAdmin />} />
        <Route path="coming-soon" element={<ComingSoonAdmin />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default function TenantSite({ slug: slugProp }) {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  return (
    <TenantProvider slug={slug}>
      <TenantRoutes />
    </TenantProvider>
  );
}

/**
 * Phase C — the practice's OWN origin (verified custom domain, or
 * `<label>.PLATFORM_DOMAIN`). Resolves which practice this host names, then renders the
 * same tenant routes as `/t/{slug}`. Unknown host -> clean "site not found", never another
 * practice's site.
 *
 * This bootstrap decides ONE thing: WHICH PRACTICE. It used to decide two, and the second
 * one was wrong: a 403 TENANT_SUSPENDED short-circuited the WHOLE host to SiteUnavailable —
 * `/admin/*` included. On a custom domain or subdomain that removed the practitioner's only
 * route to `/admin/billing`, the one prefix tenant_middleware keeps WRITABLE during a
 * non-payment cut-off (_SUSPENDED_WRITABLE_PREFIXES) precisely so paying stays possible. A
 * cut-off whose recovery page is unreachable from the practice's own address is a dead end.
 *
 * So the darkening moved down to `ServedSiteGate`, which wraps the public routes only and is
 * driven by TenantProvider's own refusal (GET /saas/tenant/{slug}, refused by the same
 * serving rule). One enforcement point, shared with the `/t/{slug}` tree; nothing about
 * suspension is decided twice.
 *
 * Identity on the refusal branch comes from GET /tenant/self/info, and it is the honest
 * source rather than a convenient one: `/api/tenant/` is in the middleware's
 * _TENANT_ADMIN_PREFIXES, so a GET survives a suspension; the endpoint is unauthenticated by
 * design (the login page has no session yet) and resolves the tenant from the Host — the
 * same place /public/site does, so no new assumption about how this origin reaches the API.
 * It is called ONLY on that branch: served traffic keeps exactly one bootstrap request.
 *
 * Falling back to the platform default (`emilie`) instead was never an option: TenantProvider
 * would then resolve ANOTHER practice, and admin/Login.jsx renders `tenant.branding.logo_url`
 * on a tenant host — a suspended practitioner's login page would wear the demo practice's
 * identity. When the slug cannot be recovered, the whole host goes dark instead (`unidentified`
 * below): no tree, rather than a tree under the wrong name.
 */
export function TenantSiteHost() {
  // `unidentified`: this host names a real practice that is NOT being served, and its
  // identity could not be recovered — distinct from `!slug`, which means no practice
  // answers to this host at all and owes the visitor "site not found".
  const [state, setState] = React.useState({ loading: true, slug: null, unidentified: false });
  React.useEffect(() => {
    let alive = true;
    const settle = (next) => { if (alive) setState({ loading: false, ...next }); };
    api.get("/public/site")
      .then((r) => settle({ slug: r.data?.slug || null, unidentified: false }))
      .catch((err) => {
        if (!isTenantSuspendedError(err)) return settle({ slug: null, unidentified: false });
        return api.get("/tenant/self/info")
          .then((r) => settle({ slug: r.data?.slug || null, unidentified: !r.data?.slug }))
          .catch(() => settle({ slug: null, unidentified: true }));
      });
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return <div data-testid="tenant-host-loading" className="min-h-screen bg-eh-bg" />;
  }
  if (state.unidentified) {
    return <SiteUnavailable />;
  }
  if (!state.slug) {
    return <SiteNotFound />;
  }
  return <TenantSite slug={state.slug} />;
}
